import configuration from '@/taskpane/common/configuration'
import state from '@/taskpane/state/sidebar-state'
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'

interface Identity {
  accessToken: string
  refreshToken: string
}

export default abstract class BaseApi {
  // class and methods ported from web portal codebase
  abstract Service: AxiosInstance

  buildService(authRequired: boolean = true, config: AxiosRequestConfig = {}) {
    const service = axios.create({
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-API-Version': 0.1,
        Pragma: 'no-cache',
        'Cache-Control': 'no-cache',
      },
      ...config,
    })

    const clientEmail = state.userEmail
    if (clientEmail) {
      service.interceptors.request.use((config) => {
        config.headers['Client-Email'] = clientEmail
        return config
      })
    }

    if (authRequired) {
      service.interceptors.request.use(async (config) => {
        const token = await state.useCirrusToken()
        if (token) {
          config.headers.Authorization = 'Bearer ' + token
        }
        return config
      })

      service.interceptors.response.use((response) => response, this.unathorizedAccessInterceptor)
    }

    return service
  }

  unathorizedAccessInterceptor = async (error: AxiosError) => {
    if (error && error.response && error.response.status === 401) {
      if (state.useCirrusToken()) {
        const token = await this.refreshAccessToken()
        if (token && error.config) {
          return this.Service.request(error.config)
        } else {
          throw error
        }
      }
    }

    return error
  }

  async refreshAccessToken(): Promise<any> {
    if (!state.refreshingToken) {
      state.refreshingToken = true
      state.resolveAccessToken = new Promise((resolve, reject) => {
        this.getNewAccessToken(state.cirrusToken, state.cirrusRefreshToken)
          .then((result) => {
            state.cirrusToken = result.accessToken
            state.cirrusRefreshToken = result.refreshToken
            resolve(result.accessToken)
          })
          .catch(async (error) => {
            state.cirrusToken = ''
            state.cirrusRefreshToken = ''
            reject(error)
          })
      })
        .catch(() => {})
        .finally(() => {
          state.refreshingToken = false
        })
    }

    return state.resolveAccessToken
  }

  getNewAccessToken(expiredAccessToken: string, refreshToken: string): Promise<Identity> {
    if (!expiredAccessToken) {
      const error = 'Access token is missing.'
      throw error
    }

    if (!refreshToken) {
      const error = 'Refresh token is missing.'
      throw error
    }

    return this.buildService(false, {
      withCredentials: true,
    })
      .post(configuration.CIRRUS_API_BASEURL + '/api/tokens/refresh', {
        Token: expiredAccessToken,
        RefreshToken: refreshToken,
      })
      .then((response) => {
        const identity: Identity = {
          accessToken: '',
          refreshToken: '',
        }

        if (response.data) {
          identity.accessToken = response.data.Token
          identity.refreshToken = response.data.RefreshToken || refreshToken
        }

        return identity
      })
  }
}
